import { Button, Divider, Input, Modal } from '@scuf/common';
import usePreventClickjacking from 'helpers/hooks/usePreventingClickjacking';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUser from 'helpers/hooks/useUser';
import { cnpjMask, COMPANY_MANAGER, cpfMask, emailMask, telephoneMask } from 'resources';
import useFetch from 'helpers/hooks/useFetch';
import { EmailServiceType } from 'models/repaircenter/detailsRma';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { useNavigate } from 'react-router-dom';

export type Participante = {
    participanteNome: string;
    participanteCPF: string;
    participanteEmail: string;
}

export type DadosCredenciamento = {
    requesterName: string;
    requesterEmail: string;
    requesterPhone: string;
    requesterPosition: string;
    companyName: string;
    companyEmail: string;
    companyPhone: string;
    companyCnpj: string;
    companyZipCode: string;
    companyStreet: string;
    companyNeighborhood: string;
    companyCity: string;
    companyState: string;
    companyCountry: string;
    companyIE: string;
    participantes: Participante[];
}

function CertifiedPage() {
    const { t } = useTranslation(['home', 'common', 'pages']);
    const navigate = useNavigate();
    const { user: userDetails } = useUser();
    const [company, setCompany] = useState<CompanyDTO>();

    const [abrirForm, setAbrirForm] = useState(false);
    const [abrirModal, setAbrirModal] = useState(false);

    const dadosResetados: DadosCredenciamento = {
        requesterName: '',
        requesterEmail: '',
        requesterPhone: '',
        requesterPosition: '',
        companyName: '',
        companyEmail: '',
        companyPhone: '',
        companyCnpj: '',
        companyZipCode: '',
        companyStreet: '',
        companyNeighborhood: '',
        companyCity: '',
        companyState: '',
        companyCountry: '',
        companyIE: '',
        participantes: [],
    };

    const [dados, setDados] = useState<DadosCredenciamento>(dadosResetados);
    const custoTotal = dados.participantes.length * 1005;

    const [novoParticipante, setNovoParticipante] = useState<Participante>({
        participanteNome: '',
        participanteCPF: '',
        participanteEmail: '',
    });

    const podeEnviar = !!(
        company
            ? (
                dados.requesterName.trim() &&
                dados.requesterEmail.trim() &&
                dados.requesterPhone.trim() &&
                dados.requesterPosition.trim() &&
                dados.companyEmail.trim() &&
                dados.companyPhone.trim() &&
                dados.participantes.length > 0
            )
            : (
                dados.requesterName.trim() &&
                dados.requesterEmail.trim() &&
                dados.requesterPhone.trim() &&
                dados.requesterPosition.trim() &&
                dados.companyName.trim() &&
                dados.companyEmail.trim() &&
                dados.companyPhone.trim() &&
                dados.companyZipCode.trim() &&
                dados.companyStreet.trim() &&
                dados.companyNeighborhood.trim() &&
                dados.companyCity.trim() &&
                dados.companyState.trim() &&
                dados.companyCountry.trim() &&
                dados.companyCnpj.trim().length === 14 &&
                dados.companyIE.trim() &&
                dados.participantes.length > 0
            )
    );

    const podeAdicionar = !!(
        novoParticipante.participanteNome.trim() &&
        novoParticipante.participanteEmail.trim() &&
        novoParticipante.participanteCPF.trim().length === 11
    );

    const [, sendEmail, sendingEmail] = useFetch<EmailServiceType<DadosCredenciamento>>({
        url: COMPANY_MANAGER.sendEmail,
        method: 'POST',
        onError: 'Error',
        onSuccess: 'Send'
    });

    const [, getCompanyById] = useFetch<{ id: number | undefined }, CompanyDTO>({
        url: COMPANY_MANAGER.getCompanyById,
        onError: t('common:errors.Unexpected')
    });

    const getUserCompanyInformations = async () => {
        await getCompanyById({ id: userDetails?.company }, current => {
            setCompany(current?.data!);
        });
    };

    const handlePartnerClick = () => {
        const loggedUser =
            userDetails &&
            Object.keys(userDetails).length > 0;

        console.log(userDetails?.company)
        if (loggedUser) {
            setAbrirForm(true);
        } else {
            setAbrirModal(true);
        }
    };

    useEffect(() => {
        document.title = t('pages:certified');
    }, []);

    useEffect(() => {
        if (userDetails?.company) {
            getUserCompanyInformations();
        }
    }, [userDetails?.company]);

    useEffect(() => {
        if (!sendingEmail) {
            setAbrirForm(false);
        }
    }, [sendingEmail]);

    useEffect(() => {
        if (!company) return;

        setDados(old => ({
            ...old,
            companyName: company.name ?? '',
            companyEmail: company.email ?? '',
            companyPhone: telephoneMask(company.phone) ?? '',
            companyCnpj: company.cnpj ?? '',
            companyIE: '',
            companyZipCode: company.zipCode ?? '',
            companyStreet: company.street ?? '',
            companyNeighborhood: company.neighborhood ?? '',
            companyCity: company.city ?? '',
            companyState: company.state ?? '',
            companyCountry: company.country ?? '',
        }));
    }, [company]);

    usePreventClickjacking();

    function adicionarParticipante() {
        setDados(old => ({
            ...old,
            participantes: [...old.participantes, novoParticipante],
        }));

        setNovoParticipante({
            participanteNome: '',
            participanteCPF: '',
            participanteEmail: '',
        });
    }

    function removerParticipante(index: number) {
        setDados(old => ({
            ...old,
            participantes: old.participantes.filter((_, i) => i !== index),
        }));
    }

    return (
        <>
            {/* Conteúdo da Página */}
            <div className="container2">
                <div className="contentPageContainer2">
                    <div className="container-grande2">
                        <h4>{t('pages:certified')}</h4>
                        <p>{t('home:certifiedPage.pageContent1') + ' '}
                            <span
                                style={{ color: 'blue', cursor: 'pointer' }}
                                onClick={() => navigate('/find-assistance')}
                            >
                                {t('home:certifiedPage.click')}
                            </span>
                            .
                        </p>

                        <h4>{t('home:certifiedPage.pageContent2')}</h4>
                        <p>{t('home:certifiedPage.pageContent3')}</p>

                        <h5>{t('home:certifiedPage.pageContent4')}</h5>
                        <ul className="column">
                            <li>{t('home:certifiedPage.pageContentUL1')}</li>
                            <li>{t('home:certifiedPage.pageContentUL2')}</li>
                            <li>{t('home:certifiedPage.pageContentUL3')}</li>
                            <li>{t('home:certifiedPage.pageContentUL4')}</li>
                            <li>{t('home:certifiedPage.pageContentUL5')}</li>
                        </ul>
                        <p>{t('home:certifiedPage.pageContent5') + ' '}
                            <span
                                style={{ color: 'blue', cursor: 'pointer' }}
                                onClick={handlePartnerClick}
                            >
                                {t('home:certifiedPage.click')}
                            </span>
                            .
                        </p>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Modal
                className="no-padding"
                open={abrirModal}
                closeIcon={true}
                onClose={() => { setAbrirModal(false) }}
            >
                <div className="modalConsultWaranty">
                    <Modal.Header>
                        <div className="modalHeader">
                            <h2>{t('home:certifiedPage.modalTitle')}</h2>
                        </div>
                    </Modal.Header>
                    <Modal.Content>
                        <p>{t('home:certifiedPage.modalContent')}</p>
                    </Modal.Content>
                    <Modal.Footer>
                        <Button
                            type="secondary"
                            content={t('common:actions.SignUp')}
                            onClick={() => setAbrirForm(true)}
                        />
                        <Button
                            type="primary"
                            content={t('common:actions.SignIn')}
                            onClick={() => window.location.href = process.env.REACT_APP_SSO_LOGIN || ''}
                        />
                    </Modal.Footer>
                </div>
            </Modal>

            {/* Formulário */}
            <Modal
                open={abrirForm}
                closeOnDimmerClick={false}
                closeOnDocumentClick={false}
                closeIcon={true}
                onClose={() => {
                    if (!sendingEmail) {
                        setAbrirForm(false);
                        setDados(dadosResetados);
                    }
                }}
            >
                <div className="modalCredenciamento">
                    <h2 className='modalHeader'>{t('home:certifiedPage.modalTitle')}</h2>
                    <Divider />
                    <Modal.Content>
                        <div className='form'>
                            <h4 style={{ margin: 0, marginBottom: 16 }}>{t('home:certifiedPage.form.requesterData')}</h4>
                            <div className='formGrid'>
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.name')}
                                    value={dados.requesterName}
                                    onChange={requesterName => setDados(old => ({ ...old, requesterName }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.position')}
                                    value={dados.requesterPosition}
                                    onChange={requesterPosition => setDados(old => ({ ...old, requesterPosition }))}
                                />
                                <Input
                                    indicator='required'
                                    label="E-mail"
                                    value={dados.requesterEmail}
                                    onChange={requesterEmail => setDados(old => ({ ...old, requesterEmail: requesterEmail.trim() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.phone')}
                                    value={telephoneMask(dados.requesterPhone)}
                                    onChange={requesterPhone => setDados(old => ({ ...old, requesterPhone }))}
                                />
                            </div>
                            <Divider />
                            <h4 style={{ margin: 0, marginBottom: 16 }}>{t('home:certifiedPage.form.companyData')}</h4>
                            <div className='formGrid'>
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.companyName')}
                                    value={dados.companyName.toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyName => setDados(old => ({ ...old, companyName: companyName.toUpperCase() }))}
                                />
                                <Input
                                    indicator='required'
                                    label="E-mail"
                                    value={dados.companyEmail}
                                    onChange={companyEmail => setDados(old => ({ ...old, companyEmail: companyEmail.trim() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.phone')}
                                    value={telephoneMask(dados.companyPhone)}
                                    onChange={companyPhone => setDados(old => ({ ...old, companyPhone }))}
                                />
                                <Input
                                    indicator='required'
                                    label={'CNPJ'}
                                    value={cnpjMask(dados.companyCnpj)}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyCnpj => setDados(old => ({ ...old, companyCnpj: companyCnpj.replace(/\D/g, '').slice(0, 14), }))}
                                />
                                <Input
                                    indicator='required'
                                    label={'Inscrição Estadual'}
                                    value={dados.companyIE}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyIE => setDados(old => ({ ...old, companyIE: companyIE.replace(/\D/g, '') }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.zipcode')}
                                    value={dados.companyZipCode.trim().toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyZipCode => setDados(old => ({ ...old, companyZipCode: companyZipCode.trim().toUpperCase() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.street')}
                                    value={dados.companyStreet.toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyStreet => setDados(old => ({ ...old, companyStreet: companyStreet.toUpperCase() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.neighborhood')}
                                    value={dados.companyNeighborhood.toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyNeighborhood => setDados(old => ({ ...old, companyNeighborhood: companyNeighborhood.toUpperCase() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.city')}
                                    value={dados.companyCity.toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyCity => setDados(old => ({ ...old, companyCity: companyCity.toUpperCase() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.state')}
                                    value={dados.companyState.toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyState => setDados(old => ({ ...old, companyState: companyState.toUpperCase() }))}
                                />
                                <Input
                                    indicator='required'
                                    label={t('home:certifiedPage.form.country')}
                                    value={dados.companyCountry.toUpperCase()}
                                    disabled={company != undefined || sendingEmail}
                                    onChange={companyCountry => setDados(old => ({ ...old, companyCountry: companyCountry.toUpperCase() }))}
                                />
                            </div>
                            <Divider />
                            <h4 style={{ margin: 0, marginBottom: 16 }}>{t('home:certifiedPage.form.participantData')}</h4>
                            <div className='participantsSection'>
                                <div className='participantRow'>
                                    <Input
                                        indicator='required'
                                        label={t('home:certifiedPage.form.name')}
                                        value={novoParticipante.participanteNome}
                                        onChange={participanteNome =>
                                            setNovoParticipante(old => ({
                                                ...old,
                                                participanteNome,
                                            }))
                                        }
                                    />
                                    <Input
                                        indicator='required'
                                        label="CPF"
                                        value={cpfMask(novoParticipante.participanteCPF)}
                                        onChange={participanteCPF =>
                                            setNovoParticipante(old => ({
                                                ...old,
                                                participanteCPF: participanteCPF.replace(/\D/g, '').slice(0, 11),
                                            }))
                                        }
                                    />
                                    <Input
                                        indicator='required'
                                        label="E-mail"
                                        value={novoParticipante.participanteEmail}
                                        onChange={participanteEmail =>
                                            setNovoParticipante(old => ({
                                                ...old,
                                                participanteEmail,
                                            }))
                                        }
                                    />
                                    <Button
                                        type="primary"
                                        content={t('common:actions.Add')}
                                        disabled={sendingEmail || !podeAdicionar}
                                        onClick={() => { adicionarParticipante() }}
                                    />
                                </div>
                                {dados.participantes.map((participante, index) => (
                                    <div
                                        key={`${participante.participanteCPF}-${index}`}
                                        className='participantRow'
                                    >
                                        <Input
                                            value={participante.participanteNome}
                                            disabled
                                        />

                                        <Input
                                            value={cpfMask(participante.participanteCPF)}
                                            disabled
                                        />

                                        <Input
                                            value={participante.participanteEmail}
                                            disabled
                                        />
                                        <Button
                                            type="secondary"
                                            content={t('common:actions.Delete')}
                                            disabled={sendingEmail}
                                            onClick={() => { removerParticipante(index) }}
                                        />
                                    </div>
                                ))}
                            </div>
                            <Divider />
                            <p style={{ margin: 0 }}>{t('home:certifiedPage.form.infoCost')}</p>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>{t('home:certifiedPage.form.cost') + custoTotal + ',00.'}</p>
                            <Divider />
                            <div className='buttons'>
                                <Button
                                    type="secondary"
                                    content={t('common:actions.Cancel')}
                                    disabled={sendingEmail}
                                    onClick={() => {
                                        if (!sendingEmail) {
                                            setAbrirForm(false);
                                            setDados(dadosResetados);
                                        }
                                    }}
                                />
                                <Button
                                    type="primary"
                                    content={sendingEmail ? t('common:actions.Sending') : t('common:actions.Send')}
                                    disabled={sendingEmail || !podeEnviar}
                                    onClick={() => {
                                        sendEmail(
                                            {
                                                NewCompany: company === undefined,
                                                Body: 'Solicitação de Credenciamento',
                                                Subject: 'Solicitação de Credenciamento',
                                                ToEmail: "Izaias.Carvalho@Honeywell.com",
                                                Credenciamento: dados,
                                            },
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </Modal.Content>
                </div>
            </Modal>
        </>
    );
}
export default CertifiedPage;